import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  AlertCircle, CheckCircle2, XCircle, Users, Briefcase, Clock,
  FileText, ClipboardCheck, MessageSquare, Video, PhoneCall, Handshake, Award,
  ChevronDown, ChevronUp,
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL;
const HIDE_KEY = 'ellas_recruitment_hide_explainer';

// Practical Test isn't listed: it's optional, only for a few craft roles,
// not a compulsory step every candidate goes through, so it doesn't belong
// in a sequence that implies "everyone goes through all of these."
const HOW_IT_WORKS = [
  { icon: FileText, label: 'Apply', tag: 'Auto', desc: 'Candidate applies on the hiring site' },
  { icon: ClipboardCheck, label: 'Aptitude Test', tag: 'Auto', desc: 'Reasoning test, scored instantly' },
  { icon: MessageSquare, label: 'Scenario Test', tag: 'Auto', desc: 'Judgment scenarios, scored instantly' },
  { icon: Video, label: 'Video Answers', tag: 'AI', desc: 'AI scores it, you confirm or override' },
  { icon: PhoneCall, label: 'Your Video Call', tag: 'You', desc: 'You interview and score here' },
  { icon: Handshake, label: 'Physical Meeting', tag: 'You', desc: 'Final in-person meeting' },
  { icon: Award, label: 'Offer', tag: 'You', desc: 'You decide, system sends it' },
];

const TAG_STYLES = {
  Auto: 'bg-gray-100 text-gray-600',
  AI: 'bg-purple-100 text-purple-700',
  You: 'bg-blue-100 text-blue-700',
};

const WALKTHROUGH_LINES = [
  'A candidate applies on the hiring site with their details, a short bio, and CV.',
  'They immediately sit the Aptitude Test, reasoning questions scored the moment they submit.',
  'If they pass, they move straight into the Scenario Test, judgment-based questions also scored instantly.',
  'Passing that opens the Video Answers stage: they record short answers on camera, AI transcribes and scores them against the rubric.',
  'You confirm or override the AI score in the Pipeline tab, that decision is what actually moves them forward.',
  'Craft roles (Stylist, Therapist, Gym Trainer, Masseuse/Nail-Facial Tech) get one extra step here, a hands-on Practical Test a staff member watches and scores directly in the admin panel.',
  'Next is your Video Call, then a Physical Meeting, both scored by you and both show up in Needs Your Review until you do.',
  'From there you decide: send an offer or reject, the system emails the candidate either way, and every step along the way sends you and them a status email.',
];

function FullWalkthrough() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-xs font-medium text-purple-700 hover:text-purple-800"
      >
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        {open ? 'Hide full walkthrough' : 'Read the full walkthrough'}
      </button>
      {open && (
        <ol className="mt-2 space-y-1.5 list-decimal list-inside">
          {WALKTHROUGH_LINES.map((line, i) => (
            <li key={i} className="text-[11px] text-gray-600 leading-snug">{line}</li>
          ))}
        </ol>
      )}
    </div>
  );
}

function daysAgo(dateStr) {
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

function HowItWorks() {
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(HIDE_KEY) === 'true'; } catch { return false; }
  });

  const toggle = () => {
    setHidden((h) => {
      try { localStorage.setItem(HIDE_KEY, String(!h)); } catch { /* ignore */ }
      return !h;
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <button onClick={toggle} className="flex w-full items-center justify-between text-left">
        <h3 className="text-sm font-semibold text-gray-700">How Hiring Works</h3>
        {hidden ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronUp size={16} className="text-gray-400" />}
      </button>
      {!hidden && (
        <>
          <p className="mt-2 text-xs text-gray-500">
            Every candidate moves through these steps in order. <strong>Auto</strong> stages score
            themselves instantly, <strong>AI</strong> stages get a first-pass score you confirm, and{' '}
            <strong>You</strong> stages are entirely yours to run and score.
          </p>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {HOW_IT_WORKS.map((step) => (
              <div key={step.label} className="flex flex-col items-start gap-1.5 rounded-lg border border-gray-100 p-3">
                <div className="flex items-center justify-between w-full">
                  <step.icon size={18} className="text-gray-500" />
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${TAG_STYLES[step.tag]}`}>{step.tag}</span>
                </div>
                <p className="text-xs font-semibold text-gray-800">{step.label}</p>
                <p className="text-[11px] text-gray-500 leading-snug">{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100 space-y-1">
            <p className="text-[11px] text-gray-500">
              <strong>Craft roles</strong> (Stylist, Therapist, Gym Trainer, Masseuse/Nail-Facial Tech)
              get one extra step here: a hands-on <strong>Practical Test</strong>, watched and scored by
              a staff member, before the video call.
            </p>
            <p className="text-[11px] text-gray-500">
              Every &ldquo;You&rdquo; stage (Practical, Video Call, Physical Meeting) shows up in{' '}
              <strong>Needs Your Review</strong> below, or in the <strong>Pipeline</strong> tab, click
              a candidate there to score them.
            </p>
          </div>
          <FullWalkthrough />
        </>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, gradient }) {
  return (
    <div className={`bg-gradient-to-br ${gradient} rounded-xl shadow-lg p-5 text-white overflow-hidden relative`}>
      <div className="absolute top-0 right-0 w-24 h-24 bg-white opacity-10 rounded-full -mr-10 -mt-10" />
      <div className="relative z-10">
        <div className="p-2.5 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm inline-flex mb-3">
          <Icon size={18} className="text-white" />
        </div>
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-xs opacity-90 mt-0.5">{label}</p>
      </div>
    </div>
  );
}

function Overview({ onOpenCandidate }) {
  const [funnel, setFunnel] = useState(null);
  const [needsReview, setNeedsReview] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [funnelRes, reviewRes, activityRes] = await Promise.all([
        axios.get(`${API_URL}/api/recruitment/funnel`),
        axios.get(`${API_URL}/api/recruitment/needs-review`),
        axios.get(`${API_URL}/api/recruitment/activity?limit=15`),
      ]);
      setFunnel(funnelRes.data);
      setNeedsReview(reviewRes.data.items);
      setActivity(activityRes.data.items);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-gray-400 text-sm">Loading overview...</p>;

  const totalActive = funnel?.byStageStatus.filter((r) => r.status === 'in_progress').reduce((a, r) => a + r.count, 0) || 0;
  const totalOffered = funnel?.byStageStatus.filter((r) => r.status === 'offered').reduce((a, r) => a + r.count, 0) || 0;
  const totalRejected = funnel?.byStageStatus.filter((r) => r.status === 'rejected').reduce((a, r) => a + r.count, 0) || 0;
  const rolesWithApplicants = new Set((funnel?.byRole || []).map((r) => r.roleKey)).size;

  return (
    <div className="space-y-6">
      <HowItWorks />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Users} label="Active Candidates" value={totalActive} gradient="from-indigo-500 to-purple-600" />
        <KpiCard icon={Briefcase} label="Roles With Applicants" value={rolesWithApplicants} gradient="from-slate-500 to-slate-700" />
        <KpiCard icon={AlertCircle} label="Awaiting Your Review" value={needsReview.length} gradient="from-amber-500 to-orange-600" />
        <KpiCard icon={CheckCircle2} label="Total Offered" value={totalOffered} gradient="from-emerald-500 to-green-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <AlertCircle size={16} className="text-amber-600" /> Needs Your Review
          </h3>
          {needsReview.length === 0 ? (
            <p className="text-sm text-gray-400">Nothing waiting on you right now.</p>
          ) : (
            <div className="space-y-2">
              {needsReview.map((item) => (
                <button
                  key={item.applicationId}
                  onClick={() => onOpenCandidate(item.applicationId)}
                  className="w-full flex items-center justify-between bg-amber-50 hover:bg-amber-100 border border-amber-100 rounded-lg px-3 py-2 text-left transition"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-800">{item.candidateName}</p>
                    <p className="text-xs text-gray-500">{item.roleLabel} · {item.stageLabel}</p>
                  </div>
                  <span className="text-xs text-amber-700 flex items-center gap-1"><Clock size={12} /> {daysAgo(item.updatedAt)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Activity</h3>
          {activity.length === 0 ? (
            <p className="text-sm text-gray-400">No activity yet.</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {activity.map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  {a.outcome === 'pass' ? (
                    <CheckCircle2 size={15} className="text-emerald-600 mt-0.5 shrink-0" />
                  ) : (
                    <XCircle size={15} className="text-red-500 mt-0.5 shrink-0" />
                  )}
                  <p className="text-gray-600">
                    <span className="font-medium text-gray-800">{a.candidateName}</span>{' '}
                    {a.outcome === 'pass' ? 'passed' : 'was rejected at'} <span className="font-medium">{a.stageLabel}</span>{' '}
                    <span className="text-gray-400">({a.roleLabel}, {daysAgo(a.updatedAt)})</span>
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {totalRejected > 0 && (
        <p className="text-xs text-gray-400">{totalRejected} candidate(s) rejected overall.</p>
      )}
    </div>
  );
}

export default Overview;
